import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface InventoryReport {
  id: number;
  productCode: string;
  productName: string;
  category: string;
  currentStock: number;
  minStockLevel: number;
  maxStockLevel: number;
  unitPrice: number;
  totalValue: number;
  lastRestockDate: string;
  stockStatus: 'Normal' | 'Low Stock' | 'Out of Stock' | 'Overstock';
  location: string;
  supplier: string;
  reorderPoint: number;
  economicOrderQuantity: number;
  leadTime: number;
  turnoverRate: number;
  lastSoldDate?: string;
  monthlyUsage: number;
  yearlyUsage: number;
}

export interface InventoryStats {
  totalProducts: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
  overstockItems: number;
  normalStockItems: number;
  totalStockQuantity: number;
  averageUnitPrice: number;
  stockTurnoverRate: number;
  categoriesBreakdown: {
    category: string;
    count: number;
    totalValue: number;
    lowStockCount: number;
    outOfStockCount: number;
  }[];
  locationsBreakdown: {
    location: string;
    productCount: number;
    totalValue: number;
    lowStockCount: number;
  }[];
  suppliersBreakdown: {
    supplier: string;
    productCount: number;
    totalValue: number;
  }[];
  topValueProducts: {
    productCode: string;
    productName: string;
    totalValue: number;
    currentStock: number;
  }[];
  criticalStockItems: {
    productCode: string;
    productName: string;
    currentStock: number;
    minStockLevel: number;
    urgency: 'High' | 'Medium' | 'Low';
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

@Injectable({
  providedIn: 'root'
})
export class InventoryReportsService {
  private apiUrl = `${environment.apiServiceBaseUrl}/inventory-reports`;

  constructor(private http: HttpClient) {}

  // Get inventory reports with optional pagination and filtering
  getInventoryReports(
    page: number = 1, 
    limit: number = 10, 
    search?: string, 
    category?: string,
    stockStatus?: string,
    location?: string,
    supplier?: string
  ): Observable<ApiResponse<InventoryReport[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (search) params = params.set('search', search);
    if (category) params = params.set('category', category);
    if (stockStatus) params = params.set('stockStatus', stockStatus);
    if (location) params = params.set('location', location);
    if (supplier) params = params.set('supplier', supplier);

    return this.http.get<ApiResponse<InventoryReport[]>>(this.apiUrl, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory report by product ID
  getInventoryReportById(id: number): Observable<ApiResponse<InventoryReport>> {
    return this.http.get<ApiResponse<InventoryReport>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory statistics and analytics
  getInventoryStats(location?: string, category?: string): Observable<ApiResponse<InventoryStats>> {
    let params = new HttpParams();
    if (location) params = params.set('location', location);
    if (category) params = params.set('category', category);

    return this.http.get<ApiResponse<InventoryStats>>(`${this.apiUrl}/stats`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get critical stock items (need immediate attention)
  getCriticalStockItems(urgency?: 'High' | 'Medium' | 'Low'): Observable<ApiResponse<InventoryReport[]>> {
    let params = new HttpParams();
    if (urgency) params = params.set('urgency', urgency);

    return this.http.get<ApiResponse<InventoryReport[]>>(`${this.apiUrl}/critical`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get low stock items
  getLowStockItems(limit: number = 50): Observable<ApiResponse<InventoryReport[]>> {
    return this.http.get<ApiResponse<InventoryReport[]>>(`${this.apiUrl}/low-stock?limit=${limit}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get out of stock items
  getOutOfStockItems(): Observable<ApiResponse<InventoryReport[]>> {
    return this.http.get<ApiResponse<InventoryReport[]>>(`${this.apiUrl}/out-of-stock`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get overstock items
  getOverstockItems(): Observable<ApiResponse<InventoryReport[]>> {
    return this.http.get<ApiResponse<InventoryReport[]>>(`${this.apiUrl}/overstock`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory categories
  getInventoryCategories(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/categories`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory locations
  getInventoryLocations(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/locations`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get stock statuses
  getStockStatuses(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/stock-statuses`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Generate inventory report for specific date
  generateInventoryReport(reportDate: string, includeAllProducts: boolean = false): Observable<ApiResponse<InventoryReport[]>> {
    return this.http.post<ApiResponse<InventoryReport[]>>(`${this.apiUrl}/generate`, {
      reportDate,
      includeAllProducts
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Calculate reorder recommendations
  getReorderRecommendations(productIds?: number[]): Observable<ApiResponse<{
    productId: number;
    productCode: string;
    productName: string;
    currentStock: number;
    recommendedOrderQuantity: number;
    reorderPoint: number;
    urgency: 'High' | 'Medium' | 'Low';
    estimatedCost: number;
    supplier: string;
    leadTime: number;
  }[]>> {
    const body = productIds ? { productIds } : {};
    return this.http.post<ApiResponse<any[]>>(`${this.apiUrl}/reorder-recommendations`, body)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update stock levels (bulk update)
  updateStockLevels(updates: {
    productId: number;
    newStockLevel: number;
    reason: string;
    adjustmentType: 'Manual' | 'Count' | 'Correction';
  }[]): Observable<ApiResponse<{
    successful: number;
    failed: {
      productId: number;
      error: string;
    }[];
  }>> {
    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/stock-levels`, { updates })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory turnover analysis
  getTurnoverAnalysis(period: 'monthly' | 'quarterly' | 'yearly' = 'monthly', months: number = 12): Observable<ApiResponse<{
    period: string;
    turnoverRate: number;
    totalValue: number;
    productCount: number;
    topPerformers: {
      productCode: string;
      productName: string;
      turnoverRate: number;
      value: number;
    }[];
    slowMovers: {
      productCode: string;
      productName: string;
      turnoverRate: number;
      lastSoldDate: string;
    }[];
  }[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/turnover-analysis?period=${period}&months=${months}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get ABC analysis (inventory classification)
  getABCAnalysis(): Observable<ApiResponse<{
    category: 'A' | 'B' | 'C';
    description: string;
    productCount: number;
    percentage: number;
    totalValue: number;
    valuePercentage: number;
    items: {
      productCode: string;
      productName: string;
      value: number;
      percentage: number;
    }[];
  }[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/abc-analysis`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Export inventory reports to Excel
  exportToExcel(search?: string, category?: string, stockStatus?: string, location?: string, supplier?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (category) params = params.set('category', category);
    if (stockStatus) params = params.set('stockStatus', stockStatus);
    if (location) params = params.set('location', location);
    if (supplier) params = params.set('supplier', supplier);

    return this.http.get(`${this.apiUrl}/export/excel`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Export inventory reports to PDF
  exportToPDF(search?: string, category?: string, stockStatus?: string, location?: string, supplier?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (category) params = params.set('category', category);
    if (stockStatus) params = params.set('stockStatus', stockStatus);
    if (location) params = params.set('location', location);
    if (supplier) params = params.set('supplier', supplier);

    return this.http.get(`${this.apiUrl}/export/pdf`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Get inventory forecast
  getInventoryForecast(productId?: number, months: number = 6): Observable<ApiResponse<{
    month: string;
    predictedDemand: number;
    predictedStock: number;
    reorderRecommendation: boolean;
    confidence: number;
  }[]>> {
    let params = new HttpParams().set('months', months.toString());
    if (productId) params = params.set('productId', productId.toString());

    return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/forecast`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Error handling
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error('Inventory Reports API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
